import { z } from 'zod/v4';
import { zodBigNumber, zodInstant, zodPlainDate } from './custom-schemas';
import { countryEnum, currencyEnum, subsidyRegimeEnum, technologyEnum } from './enums';

export type DealsViewType = 'per-deal' | 'per-delivery-dates';

import {
  type DealDeliveryMethod,
  type DealSide,
  type DealStatus,
  type DealTagOption,
  dealDeliveryMethodEnum,
  dealSideEnum,
  dealStatusEnum,
  dealTrancheStatusEnum,
  dealTrancheVolumeTypeEnum,
} from '~/api/schemas/deals.schemas';
import type { ChipProps } from '~/components/granular';

export const DEAL_TYPE_COLORS: {
  [key in DealStatus]: ChipProps['appearance'];
} = {
  DELIVERED: 'success',
  OPEN: 'info',
  PART_DELIVERED: 'warning',
} as const;

export const trancheSchema = z.object({
  client_reference: z.string().nullable(),
  contracted_unit_price: zodBigNumber.nullable(),
  created_at: zodInstant, // Will be changed when existing deals have been backfilled
  currency: currencyEnum.nullable(),
  delivered_quantity: zodBigNumber,
  delivery_method: dealDeliveryMethodEnum.nullable(),
  delivery_status: dealTrancheStatusEnum,
  id: z.uuid(),
  max_delivery_date: zodPlainDate,
  note: z.string().nullable(),
  period_end: zodPlainDate,
  period_start: zodPlainDate,
  quantity: zodBigNumber,
  registry_account_id: z.uuid().nullable(),
  requirement_production_device_client_reference: z.string().nullable(),
  requirement_production_device_id: z.uuid().nullable(),
  requirement_production_device_name: z.string().nullable(),
  requirement_production_device_registry_identifier: z.string().nullable(),
  requirement_certificate_label: z.array(z.string()).nullable(),
  requirement_certificate_type: z.string(),
  requirement_country: z.array(countryEnum).nullable(),
  requirement_minimum_commercial_operation_date: zodPlainDate.nullable(),
  requirement_sub_national_region: z.array(z.string()).nullable(),
  requirement_subsidy_regime: z.array(subsidyRegimeEnum).nullable(),
  requirement_supra_national_region: z.array(z.string()).nullable(),
  requirement_technology: z.array(technologyEnum).nullable(),
  // Every deal tag of the organisation that applies to the deal's side, by tag name.
  tags: z.record(z.string(), z.string().nullable()),
  updated_at: zodInstant,
  volume_type: dealTrancheVolumeTypeEnum,
});
export type Tranche = z.infer<typeof trancheSchema>;

export const dealSchema = z.object({
  client_reference: z.string().nullable(),
  // Optional until the backend ships these on GET /deals.
  counterparty_client_reference: z.string().optional(),
  counterparty_country: countryEnum.optional(),
  counterparty_id: z.uuid(),
  counterparty_name: z.string(),
  created_at: zodInstant,
  delivery_status: dealStatusEnum,
  executed_at: zodInstant,
  id: z.uuid(),
  side: dealSideEnum,
  total_volume: zodBigNumber,
  tranches: z.array(trancheSchema),
  updated_at: zodInstant,
});
export type Deal = z.infer<typeof dealSchema>;

export const dealListResponseSchema = z.object({ count: z.number(), deals: z.array(dealSchema) });

// flatmap schema between deal and tranches: this is what the grid will display
const dealFields = dealSchema.pick({
  client_reference: true,
  counterparty_client_reference: true,
  counterparty_country: true,
  counterparty_id: true,
  counterparty_name: true,
  executed_at: true,
  side: true,
  total_volume: true,
});

const trancheFields = trancheSchema.pick({
  client_reference: true,
  contracted_unit_price: true,
  currency: true,
  delivery_method: true,
  delivery_status: true,
  max_delivery_date: true,
  note: true,
  registry_account_id: true,
  requirement_production_device_client_reference: true,
  requirement_production_device_id: true,
  requirement_production_device_name: true,
  requirement_production_device_registry_identifier: true,
  requirement_certificate_label: true,
  requirement_certificate_type: true,
  requirement_country: true,
  requirement_minimum_commercial_operation_date: true,
  requirement_sub_national_region: true,
  requirement_subsidy_regime: true,
  requirement_supra_national_region: true,
  requirement_technology: true,
  tags: true,
});

export const dealTrancheSchema = z.object({
  deal_delivery_status: dealStatusEnum,
  generation_period_end: zodPlainDate.nullable(),
  generation_period_start: zodPlainDate.nullable(),
  id: z.string(),
  isChildRow: z.boolean(),
  tranche_client_reference: z.string().nullable(),
  tranche_id: z.uuid(),
  tranche_total_volume: zodBigNumber,
  tranche_total_volume_delivered: zodBigNumber,
  tranche_total_volume_undelivered: zodBigNumber,
  volume_type: dealTrancheVolumeTypeEnum,
  ...dealFields.shape,
  ...trancheFields.shape,
});

export type DealTranche = z.infer<typeof dealTrancheSchema>;

type DealWithTranches<TTranche extends Tranche> = Omit<Deal, 'tranches'> & { tranches: Array<TTranche> };

/**
 * Flat-maps deals to one row per tranche, which is what the deal grids display.
 * Extra fields on the tranche type (beyond `Tranche`) are kept on the row.
 */
export function flattenDealTranches<TTranche extends Tranche>(
  deals: Array<DealWithTranches<TTranche>>,
): Array<DealTranche & Omit<TTranche, keyof Tranche>> {
  return deals.flatMap((deal) =>
    deal.tranches.map(
      (tranche, index) =>
        ({
          ...tranche,
          client_reference: deal.client_reference,
          counterparty_client_reference: deal.counterparty_client_reference,
          counterparty_country: deal.counterparty_country,
          counterparty_id: deal.counterparty_id,
          counterparty_name: deal.counterparty_name,
          deal_delivery_status: deal.delivery_status,
          delivery_status: tranche.delivery_status,
          executed_at: deal.executed_at,
          generation_period_end: tranche.period_end,
          generation_period_start: tranche.period_start,
          id: deal.id,
          isChildRow: index > 0,
          side: deal.side,
          total_volume: deal.total_volume,
          tranche_client_reference: tranche.client_reference,
          tranche_delivery_status: tranche.delivery_status,
          tranche_id: tranche.id,
          tranche_total_volume: tranche.quantity,
          tranche_total_volume_delivered: tranche.delivered_quantity,
          tranche_total_volume_undelivered: tranche.quantity.minus(tranche.delivered_quantity),
          volume_type: tranche.volume_type,
        }) as DealTranche & Omit<TTranche, keyof Tranche>,
    ),
  );
}

/** The delivery methods a tranche can use, which depend on the side of its deal. */
export const DELIVERY_METHODS_BY_SIDE: Record<DealSide, Array<DealDeliveryMethod>> = {
  [dealSideEnum.enum.BUY]: [dealDeliveryMethodEnum.enum.ISSUANCE, dealDeliveryMethodEnum.enum.TRANSFER],
  [dealSideEnum.enum.SELL]: [dealDeliveryMethodEnum.enum.CANCEL, dealDeliveryMethodEnum.enum.TRANSFER],
};

/**
 * The side's delivery methods, narrowed to those the backend offers.
 */
export function getDeliveryMethodsForSide(
  side: DealSide | undefined,
  offeredMethods?: Array<DealDeliveryMethod>,
): Array<DealDeliveryMethod> {
  if (!side) {
    return [];
  }

  const sideMethods = DELIVERY_METHODS_BY_SIDE[side];
  return offeredMethods ? sideMethods.filter((method) => offeredMethods.includes(method)) : sideMethods;
}

/** The deal tags of a tranche of the side: the tranche dialog shows an input for each, and the request sends each. */
export function getDealTagsForSide(side: DealSide | undefined, dealTags: Array<DealTagOption>): Array<DealTagOption> {
  if (!side) {
    return [];
  }
  return dealTags.filter((tag) => tag.deal_sides.includes(side));
}

export function isDeliveryMethodAllowed(
  side: DealSide | undefined,
  deliveryMethod: DealDeliveryMethod | '' | null | undefined,
  offeredMethods?: Array<DealDeliveryMethod>,
) {
  return !!deliveryMethod && getDeliveryMethodsForSide(side, offeredMethods).includes(deliveryMethod);
}

/** A registry account is only meaningful on a SELL tranche delivered by transfer. */
export function hasRegistryDelivery(
  side: DealSide | undefined,
  deliveryMethod: DealDeliveryMethod | '' | null | undefined,
) {
  return side === dealSideEnum.enum.SELL && deliveryMethod === dealDeliveryMethodEnum.enum.TRANSFER;
}

export const REQUIREMENT_PRODUCTION_DEVICE_ID_KEY = 'requirement_production_device_id';

// A production device supplies these attributes itself, so a deal cannot carry both.
export const PRODUCTION_DEVICE_EXCLUSIVE_DELIVERABLE_TYPES = [
  'requirement_country',
  'requirement_minimum_commercial_operation_date',
  'requirement_sub_national_region',
  'requirement_subsidy_regime',
  'requirement_supra_national_region',
  'requirement_technology',
];

export const deliverableTypes = [
  REQUIREMENT_PRODUCTION_DEVICE_ID_KEY,
  'requirement_minimum_commercial_operation_date',
  'requirement_subsidy_regime',
  'requirement_country',
  'requirement_technology',
  'requirement_certificate_label',
  'requirement_supra_national_region',
  'requirement_sub_national_region',
  'requirement_certificate_type',
];

export const commercialOperationDateConstraint = z.enum(['BEFORE', 'ON_OR_AFTER']);
